import React, { useState } from "react";
import {
  Divider,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Box,
  IconButton,
  Typography,
  InputLabel,
  Autocomplete,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditableContainer from "../EditableContainer";
import { capitalizeFirstLetter } from "../usefulFunctions";
import { useGetLanguagesQuery } from "state/api";
import CurrencySelector from "../CurrencySelector";

const RatesPanel = ({ data, editedData, handleEditChange, changesMade }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const { data: languagesData } = useGetLanguagesQuery();

  const services = [
    "Access services",
    "Audio Description",
    "Checking/Editing",
    "Copywriting",
    "Dubbing",
    "Editing/Revision",
    "Formatting (Desktop Publishing)",
    "Interpreting",
    "Interpreting (Ad-hoc)",
    "Interpreting (Court)",
    "Interpreting (Consecutive)",
    "Interpreting (Simultaneous)",
    "Interpreting (Remote Telecom and Video)",
    "Interpreting (Whispering)",
    "Language Coaching",
    "Language Instruction",
    "Localisation",
    "Localisation (Audio Visual)",
    "Localisation (Software)",
    "Localisation (Graphics)",
    "Localisation (Website)",
    "Machine Translation Post-editing",
    "Post-editing",
    "Project Management",
    "Proofreading",
    "Sight Translation",
    "Subtitling",
    "Subtitles for the Deaf and Hard of Hearing (SDH)",
    "Summarisation",
    "Terminology Management",
    "Transcreation",
    "Transcription",
    "Translation",
    "Voiceover",
    "Other (specify in Notes)",
  ];

  const units = [
    "per source word",
    "per target word",
    "per character",
    "minimum charge",
    "per minute",
    "per page",
    "per project",
    "per document",
    "per line",
    "per hour",
    "half day",
    "full day",
    "minimum booking hours",
    "per hour overtime",
  ];

  const handleEditClick = () => {
    if (isEditMode) {
      changesMade();
    }
    setIsEditMode(!isEditMode);
  };

  const handleLanguageChange = (index, field, value) => {
    const updatedRates = [...(editedData.rates || [])];
    updatedRates[index] = {
      ...updatedRates[index],
      [field]: value,
    };
    handleEditChange("rates", updatedRates);
  };

  const handleRateChange = (index, field, value) => {
    const updatedRate = [...(editedData.rates || [])];
    updatedRate[index] = {
      ...updatedRate[index],
      [field]: value,
    };
    handleEditChange("rates", updatedRate);
  };

  const handleRemoveRate = (index) => {
    const updatedRate = [...(editedData.rates || [])];
    updatedRate.splice(index, 1);
    handleEditChange("rates", updatedRate);
  };

  const rateToDisplay = editedData.rates || data.rates;

  return (
    <EditableContainer
      title="Rates"
      isEditMode={isEditMode}
      handleEditClick={handleEditClick}
    >
      <Divider />
      <ListItem key="preferredCurrency">
        <ListItemText primary="Preferred Currency" sx={{ width: "50%" }} />
        <ListItemText sx={{ width: "50%" }}>
          {isEditMode ? (
            <Box>
              <CurrencySelector
                selectedCurrency={editedData.preferredCurrency}
                onSelectCurrency={(currency) =>
                  handleEditChange("preferredCurrency", currency)
                }
              />
            </Box>
          ) : (
            editedData.preferredCurrency ?? data.preferredCurrency
          )}
        </ListItemText>
      </ListItem>
      <Divider />

      {isEditMode ? (
        <>
          {editedData &&
            editedData.rates &&
            editedData.rates.map((rate, index) => (
              <Box key={`rate-box-${index}`}>
                <ListItem key={`rate-${index}`}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      width: "100%",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "calc(100% - 48px)",
                        mr: 2,
                      }}
                    >
                      {/* Languages row */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          width: "100%",
                        }}
                      >
                        <Autocomplete
                          options={languagesData || []}
                          getOptionLabel={(option) => option.name}
                          value={rate.sourceLanguage || null}
                          onChange={(e, newValue) =>
                            handleLanguageChange(
                              index,
                              "sourceLanguage",
                              newValue
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Select source language"
                            />
                          )}
                          sx={{ width: "50%", mr: 2 }}
                        />
                        <Autocomplete
                          options={languagesData || []}
                          getOptionLabel={(option) => option.name}
                          value={rate.targetLanguage || null}
                          onChange={(e, newValue) =>
                            handleLanguageChange(
                              index,
                              "targetLanguage",
                              newValue
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Select target language"
                            />
                          )}
                          sx={{ width: "50%" }}
                        />
                      </Box>

                      {/* Service, Daily capacity, Cost, Unit */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          width: "100%",
                          mt: 1,
                          gap: 2,
                        }}
                      >
                        {/* Service (formerly Category) */}
                        <ListItemText sx={{ width: "30%" }}>
                          <FormControl sx={{ width: "100%" }}>
                            <InputLabel htmlFor="rates-service">
                              Service
                            </InputLabel>
                            <Select
                              label="Service"
                              value={rate.category || ""} // keep underlying key 'category' for compatibility
                              inputProps={{
                                name: "ratesService",
                                id: "rates-service",
                              }}
                              onChange={(e) =>
                                handleRateChange(
                                  index,
                                  "category",
                                  e.target.value
                                )
                              }
                            >
                              {services.map((service) => (
                                <MenuItem
                                  key={service.toLowerCase()}
                                  value={service.toLowerCase()}
                                >
                                  {service}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </ListItemText>

                        {/* Daily capacity (new) */}
                        <ListItemText sx={{ width: "25%" }}>
                          <TextField
                            fullWidth
                            label="Daily capacity"
                            placeholder="e.g. 2500 words"
                            value={rate.dailyCapacity ?? ""}
                            onChange={(e) =>
                              handleRateChange(
                                index,
                                "dailyCapacity",
                                e.target.value
                              )
                            }
                          />
                        </ListItemText>

                        {/* Cost */}
                        <ListItemText sx={{ width: "20%" }}>
                          <TextField
                            fullWidth
                            label="Cost"
                            value={rate.cost ?? ""}
                            onChange={(e) =>
                              handleRateChange(index, "cost", e.target.value)
                            }
                            inputProps={{ inputMode: "decimal" }}
                          />
                        </ListItemText>

                        {/* Unit */}
                        <ListItemText sx={{ width: "25%" }}>
                          <FormControl sx={{ width: "100%" }}>
                            <InputLabel htmlFor="rates-unit">Unit</InputLabel>
                            <Select
                              label="Unit"
                              value={rate.unit || ""}
                              inputProps={{
                                name: "ratesUnit",
                                id: "rates-unit",
                              }}
                              onChange={(e) =>
                                handleRateChange(index, "unit", e.target.value)
                              }
                            >
                              {units.map((unit) => (
                                <MenuItem key={unit} value={unit}>
                                  {unit}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </ListItemText>
                      </Box>
                    </Box>

                    {/* Delete button */}
                    <Box
                      sx={{
                        flexShrink: 0,
                        alignSelf: "stretch",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ListItemText sx={{ width: "1%", ml: "1rem" }}>
                        <IconButton
                          onClick={() => handleRemoveRate(index)}
                          color="error"
                          aria-label="delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemText>
                    </Box>
                  </Box>
                </ListItem>
                <Divider />
              </Box>
            ))}

          {/* Add new row */}
          <ListItem sx={{ justifyContent: "center" }}>
            <Stack>
              {editedData.rates?.length < 1 && (
                <Typography variant="caption">
                  Add your first rate using the button below
                </Typography>
              )}
              <Box
                sx={{ display: "flex", justifyContent: "center", mt: "0.5rem" }}
              >
                <IconButton
                  aria-label="add"
                  onClick={() =>
                    handleEditChange("rates", [
                      ...(editedData.rates || []),
                      {
                        category: "",
                        dailyCapacity: "",
                        cost: 0,
                        unit: "",
                      },
                    ])
                  }
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Stack>
          </ListItem>
        </>
      ) : (
        // Display mode
        <>
          {rateToDisplay && rateToDisplay.length > 0 ? (
            rateToDisplay.map((rate, index) => (
              <Box key={`rate-box-${index}`} sx={{ mb: 2 }}>
                <ListItem>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                      alignItems: "center",
                    }}
                  >
                    {/* Languages */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: "100%",
                      }}
                    >
                      <ListItemText sx={{ width: "50%" }}>
                        {rate.sourceLanguage?.name}
                      </ListItemText>
                      <ListItemText sx={{ width: "25%" }}>
                        {rate.sourceLanguage?.name && rate.targetLanguage?.name
                          ? "into"
                          : ""}
                      </ListItemText>
                      <ListItemText sx={{ width: "25%" }}>
                        {rate.targetLanguage?.name}
                      </ListItemText>
                    </Box>

                    {/* Service + capacity + price */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: "100%",
                      }}
                    >
                      <ListItemText sx={{ width: "40%" }}>
                        {rate.category
                          ? `Service: ${capitalizeFirstLetter(rate.category)}`
                          : ""}
                      </ListItemText>
                      <ListItemText sx={{ width: "30%" }}>
                        {rate.dailyCapacity
                          ? `Daily capacity: ${rate.dailyCapacity}`
                          : ""}
                      </ListItemText>
                      <ListItemText sx={{ width: "15%" }}>
                        {rate.cost ? `£${rate.cost}` : ""}
                      </ListItemText>
                      <ListItemText sx={{ width: "15%" }}>
                        {capitalizeFirstLetter(rate.unit)}
                      </ListItemText>
                    </Box>
                  </Box>
                </ListItem>
                <Divider />
              </Box>
            ))
          ) : (
            <ListItem>
              <ListItemText>No rates added</ListItemText>
            </ListItem>
          )}
        </>
      )}
    </EditableContainer>
  );
};

export default RatesPanel;
